import React, { useState, useEffect, useRef } from 'react';
import type jsPDF from 'jspdf';
import { Report, ProjectData, RabEntry, UnterlageVersion, UserProfile } from '../../types';
import { Button } from '../Button';
import { Sparkles, BookOpen, Settings as SettingsIcon, Copy, Edit2, FileCheck, Loader2, Save, Trash2, History, Upload, FileDown, X, FolderOpen, FolderInput, CheckCircle2, AlertTriangle, RotateCw } from 'lucide-react';

// Import UfsA Components (same folder)
import { ProjectForm } from './UfsaProjectForm';
import { EntryForm } from './UfsaEntryForm';
import { EntryList } from './UfsaEntryList';
import { UfsaPdfPreview } from './UfsaPdfPreview';
import { DEFAULT_CATEGORY_ORDER, autoCategorize, categorizeNewEntry, categoryKey, categoryOrderWithExtras, moveEntryToCategory, moveEntryWithinCategory } from './ufsaOrdering';
import { anschriftFuerProjekt, getInitialProjectData, projektdatenAbweichungen, uebernehmeProjektdaten, type ProjektdatenFeld } from './ufsaProjectData';
import { AppendixManager } from './UfsaAppendixManager';
import { MitgeltendeUnterlagenPanel } from './UfsaMitgeltendeUnterlagenPanel';
import { UfsaPlanungFragenKarten } from './UfsaPlanungFragenKarten';
import { UfsaPlanungUnterlagenVorschlaege } from './UfsaPlanungUnterlagenVorschlaege';
import { UfsaProjektdatenHinweis } from './UfsaProjektdatenHinweis';
import { pflichtAnzeige, type UfsaEintragVorbelegung, type UfsaPflichtTon } from './ufsaPlanung';
import { useUfsaPlanung } from './hooks/useUfsaPlanung';
import { useProjectAnalysis } from './hooks/useProjectAnalysis';
import { useRechte } from '../../hooks/useRechte';
import { AiAssistant } from './UfsaAiAssistant';
import { ChangelogModal } from './UfsaChangelogModal';
import { WikiModal } from './UfsaWikiModal';
import { SettingsModal } from './UfsaSettingsModal';
import { CoverDesignSelector } from './UfsaCoverDesignSelector';
import { BillingSelectionModal } from '../BillingSelectionModal';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { AbweichungsHinweis } from '../common/AbweichungsHinweis';
import {
    UfsaAblageAnlass,
    UfsaAusgabeOptionen,
    ufsaAblageAnlassFuerExport,
    ufsaAblageAnlassVonHand,
    ufsaAblageAuftrag,
    ufsaAblageHinweise,
    ufsaAblageWarnung,
    ufsaHeuteIso,
} from './ufsaAblage';

// Native PDF generator (replaces html2canvas)
import { generateUnterlagePdf } from '../../services/pdf/generatorUnterlage';
import { auftragFuerWiederholung, legeUnterlageAb, UnterlageAblegen } from '../../services/unterlagenAblage';
import { holdIdleExit } from '../../services/idleHold';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { safeGetItem, safeSetItem } from '../../services/safeStorage';
import { isHourlyUnit } from '../../services/projectLv';
import { selectBillableServices } from '../../services/billingSelection';
import { useBestandteilAbschluss } from '../bestandteile/useBestandteilAbschluss';
import { BestandteilAbschlussHinweis } from '../bestandteile/BestandteilAbschlussHinweis';
import { BestandteilStundenPflicht } from '../bestandteile/BestandteilStundenPflicht';
import { bestimmeAbschlussFall, mitPositionsauswahl, schonGebuchtHinweis } from '../bestandteile/abschlussLogic';
import { exportBuchungStand, projektDesVorgangs, type ExportBuchungStand } from '../bestandteile/abschlussSchreibweg';
import { JournalMirrorRequest, queueJournalMirror } from '../../services/serviceJournal';
import {
    appendUnterlageVersion,
    autoBackupLabel,
    createUnterlageVersion,
    formatVersionTimestamp,
    removeUnterlageVersion,
    restoreUnterlageVersion,
    withLiveAppendixImages,
} from '../../services/documentVersioning';

interface Props {
    report: Report;
    onUpdate: (report: Report) => void;
    onBack: () => void;
    saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
    // NEW: Controlled by Sidebar
    activeSection?: string;
    onSectionChange?: (section: string) => void;
    /**
     * Nur fuer die eingebettete Projektablage: sie vermerkt, WER hochgeladen
     * hat, und sperrt im Lesemodus das Ablegen. Beides betrifft ausschliesslich
     * das Dokumentenmodul — das uebrige Verhalten dieser Ansicht bleibt
     * unberuehrt (die Behoerdenliste schreibt bewusst weiterhin auch im
     * Lesemodus, siehe App.tsx).
     */
    userProfile?: UserProfile | null;
    isReadOnly?: boolean;
}

/**
 * Was die Projektablage zuletzt mit dieser Unterlage gemacht hat — sichtbar
 * unter der Werkzeugleiste. Ein Export bleibt gueltig, auch wenn die Ablage
 * scheitert; dann steht hier der Grund und der Knopf „Erneut ablegen".
 *
 * Im Fehlerfall haelt der Stand den vollstaendigen AUFTRAG fest (Bytes und
 * Kennzeichen des exportierten Blattes): „Erneut ablegen" schickt genau ihn
 * noch einmal und setzt NICHT den dann aktuellen Editorstand neu — sonst
 * fehlte in der Ablage weiterhin das Blatt, das wirklich exportiert (und
 * abgerechnet) wurde. `auftrag: null` nur, wenn schon das Ausgeben der Bytes
 * scheiterte; dann gibt es nichts zu wiederholen, nur den Knopf von Hand.
 * `uebersprungen` ist kein Fehler: Lesemodus oder ungespeichertes Projekt.
 */
type AblageStand =
    | { status: 'laeuft'; anlass: UfsaAblageAnlass }
    /**
     * `warnung`: bei `schon_abgelegt` der Satz des Kerns, wenn die doppelt
     * hochgeladene Datei nicht wieder entfernt werden konnte (verwaiste Datei
     * im Bucket). Die Fassung liegt — aber gelb, nicht gruen, und ohne Retry.
     */
    | {
        status: 'abgelegt' | 'schon_abgelegt'; anlass: UfsaAblageAnlass; titel: string; warnung: string | null;
        /** Saetze des Kerns, die keine Fehler sind (etwa zum Journal-Eintrag, Stufe 6.3) — dezent darunter. */
        hinweise: string[];
    }
    | { status: 'fehler'; anlass: UfsaAblageAnlass; fehler: string; auftrag: UnterlageAblegen | null }
    | { status: 'uebersprungen'; grund: string };

/** Farben der Pflichtstand-Marke im Kopf — `ungeklaert` ist gelb, nie grau: aus Unklarheit folgt keine Entwarnung. */
const PFLICHT_KLASSE: Record<UfsaPflichtTon, string> = {
    laedt: 'bg-slate-50 border-slate-200 text-slate-500',
    pflicht: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    ungeklaert: 'bg-amber-50 border-amber-200 text-amber-900',
    keine: 'bg-slate-100 border-slate-200 text-slate-600',
};


export const UnterlageView: React.FC<Props> = ({
    report, onUpdate,
    activeSection = 'project', onSectionChange,
    userProfile, isReadOnly = false
}) => {
    
    const currentTab = activeSection;

    const [projectData, setProjectData] = useState<ProjectData>(() => getInitialProjectData(report));
    const [entries, setEntries] = useState<RabEntry[]>(report.unterlage?.rabEntries || []);
    // Versionen liegen im lokalen State und werden bei JEDEM Autospeichern
    // mitgeschrieben — sonst könnte ein bereits laufender Autospeicher-Timer
    // (der noch den alten `report` in seiner Closure hält) eine gerade
    // gespeicherte Version wieder entfernen.
    //
    // Ablage: report.unterlageVersions, also im JSON des Projekts. Anders als
    // der LEBENDE Stand (rabEntries/appendixImages liegen normalisiert in
    // ufsa_rab_entries bzw. ufsa_appendix_images und werden aus dem JSON
    // entfernt) trägt eine Version ihre Textinhalte selbst — ein eingefrorener
    // Stand darf sich nicht ändern, wenn das lebende Dokument sich ändert.
    //
    // AUSNAHME Anhangsbilder: die bleiben aus der Version heraus (base64 würde
    // die Verschlankung des JSON aushebeln) und werden überall dort, wo aus
    // einer Version wieder ein Dokument wird, aus dem laufenden Stand ergänzt.
    const [versions, setVersions] = useState<UnterlageVersion[]>(report.unterlageVersions || []);
    const [showVersionDialog, setShowVersionDialog] = useState(false);
    const [showVersionPanel, setShowVersionPanel] = useState(false);
    const [versionLabel, setVersionLabel] = useState('');

    const [showChangelog, setShowChangelog] = useState(false);
    const [showWiki, setShowWiki] = useState(false);
    // Die Projektablage, eingebettet in die Unterlage.
    const [dokumenteOffen, setDokumenteOffen] = useState(false);
    const [ablageStand, setAblageStand] = useState<AblageStand | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showDesignSelector, setShowDesignSelector] = useState(false);
    const [showInitDialog, setShowInitDialog] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    
    const [isDarkMode, setIsDarkMode] = useState(false);
    // Querformat-Einstellung überlebt das Schließen des Moduls (safeStorage)
    const [isExperimental, setIsExperimental] = useState(() => safeGetItem('ufsa.landscape') === '1');
    const toggleLandscape = () => {
        setIsExperimental(prev => {
            safeSetItem('ufsa.landscape', prev ? '0' : '1');
            return !prev;
        });
    };
    const [showRegulations] = useState(true);
    // Reihenfolge der Kategorien: liegt in projectData (und damit im Projekt
    // und in jeder eingefrorenen Version), nicht mehr nur im Zustand dieser
    // Ansicht — vorher war sie nach jedem Öffnen wieder die Standardliste,
    // und das PDF kannte sie gar nicht. Selbst angelegte Kategorien hängen
    // hinten an, damit der Einstellungsdialog auch sie verschieben kann.
    const categoryOrder = categoryOrderWithExtras(projectData.categoryOrder || DEFAULT_CATEGORY_ORDER, entries);
    const setCategoryOrder = (order: string[]) => handleProjectChange('categoryOrder', order);
    
    const [showBillingModal, setShowBillingModal] = useState(false);
    // Preise im Abrechnungsdialog nur mit dem Recht „Preise sehen" (Admins immer).
    const rechte = useRechte(userProfile?.role);
    /**
     * Abschlussschritt nach der FREIGABE (Plan Paketposition B5): die finale
     * Fassung wird beim Export als freigegeben abgelegt; die Ausgabe, die dabei
     * entsteht, erledigt den Bestandteil „Unterlage" per Trigger, und der Hook
     * bucht ein dadurch vollstaendiges Paket (Fall 2/3).
     */
    const abschluss = useBestandteilAbschluss({ report, onUpdate, userProfile, quelle: 'unterlage', isReadOnly });
    /** Eine Exportbuchung, die NICHT geschrieben wurde (Projekt gewechselt, Ansicht verlassen) — sichtbar, nie still. */
    const [exportBuchungHinweis, setExportBuchungHinweis] = useState<string | null>(null);

    const analysis = useProjectAnalysis(projectData, entries);
    const defaultPreamble = report.settings?.moduleSettings?.ufsa?.defaultPreamble;

    // ═══════════════════════════════════════════════════════════
    // PLANUNGSPHASE (Stufe 3.4) — nur lesen, Uebernahme nur per Klick
    // ═══════════════════════════════════════════════════════════
    const projektIdFuerPlanung = (report.metadata.globalData?.projectId || '').trim();
    const planung = useUfsaPlanung(projektIdFuerPlanung, report.metadata);
    const pflicht = pflichtAnzeige(planung);
    /**
     * Nach „Neue Fassung anlegen" im Abweichungshinweis (Stufe 6.1). Die
     * Unterlage kennt keinen eigenen Entwurf neben der geltenden Fassung;
     * der vorhandene Weg ist „Version speichern": der bisherige Stand wird
     * eingefroren, danach wird am laufenden Dokument weitergearbeitet, und
     * der naechste Export ist die neue Fassung. Der Dialog wird geoeffnet,
     * gespeichert wird erst mit seinem Knopf.
     */
    const [neueFassungHinweis, setNeueFassungHinweis] = useState(false);
    const handleNeueFassungAusPlanung = () => {
        if (isReadOnly) return;
        setVersionLabel('Fassung vor Änderung aus der Planungsphase');
        setShowVersionDialog(true);
        setNeueFassungHinweis(true);
    };
    // Gebaeudetyp nur, wenn aus der zustaendigen Quelle belegt — ein
    // Ersatzwert waehlt keine Vorlage.
    const gebaeudetyp = planung.steckbrief?.gebaeudetyp.stand === 'belegt' ? planung.steckbrief.gebaeudetyp.wert : null;

    // Vorbelegung des Eintragsformulars aus einer Karte „Aus der Planungsphase".
    // Der Schluessel wechselt je Klick, damit das Formular auch dieselbe Frage
    // ein zweites Mal uebernimmt. Gespeichert wird erst im Formular.
    const [vorbelegung, setVorbelegung] = useState<UfsaEintragVorbelegung | null>(null);
    const handleFrageVorbelegen = (eintrag: Omit<RabEntry, 'id'>, frage: { code: string }) => {
        setVorbelegung({ key: crypto.randomUUID(), eintrag, herkunft: frage.code });
        document.getElementById('catalog-component')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Veraltete Kopie: Bauherr und Verteiler werden nur beim ersten Oeffnen
    // kopiert (getInitialProjectData). Hier wird nur VERGLICHEN; uebernommen
    // wird je Feld per Klick.
    const abweichungen = projektdatenAbweichungen(projectData, report.metadata);
    const handleProjektdatenUebernehmen = (feld: ProjektdatenFeld) => {
        if (isReadOnly) return;
        setProjectData(prev => uebernehmeProjektdaten(prev, report.metadata, feld));
    };
    // Anschrift des Bauherrn: der Autospeicher unten schreibt sie nur in die
    // Projektdaten zurueck, solange dieses Flag steht — gesetzt bei jeder
    // Bearbeitung HIER (handleProjectChange, Version laden), zurueckgesetzt
    // erst, nachdem der Autospeicher sie weitergereicht hat. So kommt auch
    // die Rueckkorrektur A → B → A im Projekt an; ohne Bearbeitung bleibt die
    // Anschrift des Projekts stehen (sonst ueberschriebe die beim ersten
    // Oeffnen kopierte Anschrift zwei Sekunden nach dem Oeffnen die inzwischen
    // im Projekt gepflegte — und der Hinweis oben verschwaende mit dem Wert,
    // den er zeigen soll). Ein Ref, kein Zustand: das Flag soll nichts zeichnen.
    const anschriftBearbeitet = useRef(false);

    useEffect(() => {
        // Bereits abgerechnet? Die Frage lautet „ist in DIESEM Modul schon etwas
        // erbracht?" — sie gilt der GANZEN Auswahl, nie einer herausgegriffenen
        // Zeile. `[0]` hing an der Reihenfolge der Leistungsliste, und die kommt
        // ungeordnet aus der Datenbank: bei „Prüfung der Ausführungsunterlagen"
        // (0) vor „Unterlage erstellen (RAB 32)" (1) blieb der Dialog aus,
        // obwohl die Unterlage laengst erstellt und abgerechnet war.
        // Positionsnummern spielen bewusst keine Rolle (siehe selectBillableServices).
        const services = report.metadata.globalData?.services || [];
        const alreadyBilled = selectBillableServices(services, 'unterlage')
            .some(s => (s.quantityUsed ?? 0) > 0);

        // Leere Auswahl → `some` ist false → kein Dialog. Genau wie bisher.
        if (alreadyBilled) {
            setShowInitDialog(true);
        }
    }, []);
    
    useEffect(() => {
        const meta = report.metadata;
        let hasChanges = false;
        const newData = { ...projectData };

        if (meta.projectName && newData.projectName !== meta.projectName) { newData.projectName = meta.projectName; hasChanges = true; }
        if (meta.projectAddress && newData.location !== meta.projectAddress) { newData.location = meta.projectAddress; hasChanges = true; }
        if (meta.clientName && newData.builder !== meta.clientName) { newData.builder = meta.clientName; hasChanges = true; }
        if (meta.coordinator && newData.coordinator !== meta.coordinator) { newData.coordinator = meta.coordinator; hasChanges = true; }
        if (meta.coordinatorId && newData.coordinatorId !== meta.coordinatorId) { newData.coordinatorId = meta.coordinatorId; hasChanges = true; }
        
        if (hasChanges) {
            setProjectData(newData);
        }
    }, [report.metadata]);

    // Der Bericht von JETZT, nicht der aus dem Render, in dem der Timer armiert
    // wurde. Ohne diesen Ref war der Autospeicher unten ein Datenverlust:
    // sein Debounce haengt nur an projectData/entries/versions, der Export mit
    // Abrechnung (executePdfGeneration) aendert aber KEINEN davon — er schreibt
    // Buchung und serviceLog ueber onUpdate und laesst die Ansicht offen. Der
    // wartende Timer lief deshalb nicht neu an, feuerte mit dem alten `report`
    // aus seiner Closure, und App.handleUpdateReport ERSETZT vollstaendig
    // (setReport + offlineSync.enqueue, es wird nichts gemergt): die gerade
    // gebuchte Leistung war aus dem Protokoll verschwunden — waehrend die
    // Journal-Vormerkung stehen blieb. Wer den Ref zurueckbaut, holt sich
    // beides zurueck.
    const reportRef = useRef(report);
    reportRef.current = report;

    useEffect(() => {
        const timer = setTimeout(() => {
            // Bewusst erst HIER gelesen — beim Feuern, nicht beim Armieren.
            const aktuell = reportRef.current;
            // Nur eine in dieser Ansicht bearbeitete Anschrift geht zurueck ins
            // Projekt (siehe `anschriftBearbeitet`); sonst bleibt die des Projekts.
            const anschriftZurueckschreiben = anschriftBearbeitet.current;
            onUpdate({
                ...aktuell,
                metadata: {
                    ...aktuell.metadata,
                    projectName: projectData.projectName,
                    projectAddress: projectData.location,
                    clientName: projectData.builder,
                    clientAddress: anschriftFuerProjekt(anschriftZurueckschreiben, projectData.builderAddress, aktuell.metadata.clientAddress),
                    coordinator: projectData.coordinator,
                    coordinatorId: projectData.coordinatorId
                },
                unterlage: {
                    projectData,
                    rabEntries: entries,
                    entries: []
                },
                unterlageVersions: versions
            });
            // Erst NACH dem Weiterreichen zuruecksetzen: wirft onUpdate, bleibt
            // das Flag stehen und der naechste Lauf schreibt erneut.
            if (anschriftZurueckschreiben) anschriftBearbeitet.current = false;
        }, 2000);
        // Raeumt beim Verlassen der Ansicht ab (App haengt UnterlageView beim
        // Wechsel der view aus) und vor jedem Neuarmieren — der Autospeicher
        // schreibt dadurch nicht oefter als zuvor, nur nie mehr veraltet.
        return () => clearTimeout(timer);
    }, [projectData, entries, versions]);

    const handleProjectChange = (key: keyof ProjectData, value: any) => {
        // Anschrift des Bauherrn hier bearbeitet → der Autospeicher schreibt sie
        // ins Projekt zurueck (auch eine Rueckkorrektur auf den alten Wert).
        if (key === 'builderAddress') anschriftBearbeitet.current = true;
        if (key === 'coordinator' && value !== report.metadata.coordinator) {
             setProjectData(prev => ({ ...prev, [key]: value, coordinatorId: undefined }));
        } else {
             setProjectData(prev => ({ ...prev, [key]: value }));
        }
    };

    const handleClearCatalog = () => {
        if (window.confirm("Möchten Sie den gesamten Maßnahmenkatalog wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.")) {
            setEntries([]);
        }
    };

    const handleAddEntry = (entry: Omit<RabEntry, 'id'>, opts?: { categoryByHand?: boolean }) => {
        const categorizedEntry = categorizeNewEntry(entry, opts?.categoryByHand === true);
        const newEntry = { ...categorizedEntry, id: crypto.randomUUID() };
        setEntries(prev => [...prev, newEntry]);
    };

    const handleAddMultipleEntries = (newEntries: Omit<RabEntry, 'id'>[]) => {
        const withIds = newEntries.map(e => ({ ...autoCategorize(e), id: crypto.randomUUID() }));
        setEntries(prev => [...prev, ...withIds]);
    };

    const handleUpdateEntry = (id: string, updated: Omit<RabEntry, 'id'>) => {
        setEntries(prev => prev.map(e => {
            if (e.id !== id) return e;
            // Von Hand gewechselte Kategorie gilt — autoCategorize setzte sie
            // sonst still zurück (z. B. „elektrische" → immer Technik).
            const categoryChanged = categoryKey(updated.category) !== categoryKey(e.category);
            return { ...(categoryChanged ? updated : autoCategorize(updated)), id };
        }));
    };

    const handleDeleteEntry = (id: string) => {
        if(confirm('Eintrag wirklich löschen?')) {
            setEntries(prev => prev.filter(e => e.id !== id));
        }
    };

    // Verschiebt innerhalb der Kategorie (siehe ufsaOrdering) — der frühere
    // Tausch in der flachen Liste war in der gruppierten Ansicht unsichtbar.
    const handleMoveEntry = (id: string, direction: 'up' | 'down') => {
        setEntries(prev => moveEntryWithinCategory(prev, id, direction));
    };

    // Wechsel in eine andere Kategorie (z. B. Wand → Dach), dort ans Ende —
    // bewusst ohne autoCategorize, die Wahl des Anwenders gilt.
    const handleMoveEntryToCategory = (id: string, category: string) => {
        setEntries(prev => moveEntryToCategory(prev, id, category));
    };

    const handleRenameCategory = (oldName: string, newName: string) => {
        const key = categoryKey(oldName);
        setEntries(prev => prev.map(e => categoryKey(e.category) === key ? { ...e, category: newName.trim() } : e));
    };

    const handleFixNavigation = (field: string) => {
        if (onSectionChange) {
             if (field.includes('Bild') || field.includes('Risiko')) onSectionChange('appendix');
             else if (field.includes('Maßnahmen') || field.includes('Kategorie')) onSectionChange('catalog');
             else onSectionChange('project');
        }
    };

    /**
     * Der Abschlussschritt beginnt mit dem Klick (Plan B5): nur bei einer
     * EIGENEN Unterlagen-Position (Fall 1/3) die Positionsauswahl wie bisher.
     * Fall 2 (nur ein Paket) und „weder noch": kein Auswahldialog — der Export
     * laeuft ohne Buchung; eine finale Fassung wird trotzdem als Freigabe
     * abgelegt (ufsaAblageAnlassFuerExport), und deren Ausgabe erledigt den
     * Bestandteil. Nur-Ansicht/Freelancer: keine Auswahl.
     */
    const handleGenerateClick = () => {
        const fall = bestimmeAbschlussFall({ positionen: billingServices, pakete: abschluss.pakete });
        if (!abschluss.sperre && mitPositionsauswahl(fall)) {
            setShowBillingModal(true);
            return;
        }
        void executePdfGeneration(null);
    };

    // ═══════════════════════════════════════════════════════════
    // PROJEKTABLAGE („Dokumente", Stufe 1A)
    // ═══════════════════════════════════════════════════════════

    /**
     * Die Generatoroptionen, die das Blatt veraendern — gehen in die Kennung
     * des Standes (ufsaFassungRef) und muessen DIESELBEN sein wie beim Setzen.
     */
    const ausgabeOptionen = (): UfsaAusgabeOptionen => ({
        showRegulations,
        orientation: isExperimental ? 'landscape' : 'portrait',
        coverImageSize: projectData.coverImageSize || 'medium',
    });

    /**
     * Schickt einen fertigen Auftrag an die Projektablage. Wirft nie
     * (legeUnterlageAb wirft nicht) und laeuft NEBEN dem Export: ein PDF, das
     * der Anwender schon in der Hand hat, und eine Buchung, die schon
     * geschrieben ist, bleiben gueltig, wenn die Ablage scheitert — dann steht
     * der Grund unter der Leiste, und „Erneut ablegen" schickt DENSELBEN
     * Auftrag noch einmal.
     */
    const sendeAblageAuftrag = async (auftrag: UnterlageAblegen, anlass: UfsaAblageAnlass) => {
        setAblageStand({ status: 'laeuft', anlass });
        // Haelt den Leerlauf-Rueckzug waehrend des Uploads zu — wie der
        // Upload im Dokumentenmodul.
        const freigeben = holdIdleExit('Ablage der Unterlage läuft');
        try {
            const ergebnis = await legeUnterlageAb(auftrag);
            if (ergebnis.status === 'fehler') {
                // Der Auftrag fuer „Erneut ablegen" traegt den Steckbrief-Anker
                // DIESES Versuchs: wiederholt wird der Stand der Ausgabe, nicht
                // der inzwischen geaenderte.
                setAblageStand({
                    status: 'fehler', anlass, auftrag: auftragFuerWiederholung(auftrag, ergebnis),
                    fehler: ergebnis.fehler || 'Die Ablage hat keinen Grund genannt.',
                });
            } else {
                setAblageStand({
                    status: ergebnis.status, anlass, titel: auftrag.titel,
                    warnung: ufsaAblageWarnung(ergebnis), hinweise: ufsaAblageHinweise(ergebnis),
                });
                // Jede FREIGABE ist eine Ausgabe — auch die von Hand abgelegte
                // und „Erneut ablegen". Der Trigger entscheidet, ob sie einen
                // Bestandteil erledigt; nachgefragt wird mit ihrer Kennung.
                if (anlass === 'freigabe') void abschluss.nachAblage(ergebnis);
            }
        } finally {
            freigeben();
        }
    };

    /**
     * Baut aus dem GESETZTEN Dokument den Auftrag (Bytes + Kennzeichen des
     * exportierten Blattes, ufsaAblageAuftrag) und schickt ihn. Wann der
     * Export automatisch hier landet, entscheidet ufsaAblageAnlassFuerExport
     * (Entscheid E17: Fassungen und abgerechnete Entwuerfe; blosse Entwuerfe
     * nur ueber den Knopf). Die Vorschau legt nie ab.
     */
    const legeStandAb = (
        doc: jsPDF,
        anlass: UfsaAblageAnlass,
        a: { gebucht: boolean; vonHand: boolean },
    ) => {
        const projektId = (report.metadata.globalData?.projectId || '').trim();
        if (!projektId || projektId === 'temp-id') {
            setAblageStand({ status: 'uebersprungen', grund: 'Nicht in „Dokumente" abgelegt: das Projekt ist noch nicht gespeichert.' });
            return;
        }
        if (isReadOnly) {
            setAblageStand({ status: 'uebersprungen', grund: 'Nur-Ansicht: die Unterlage wurde nicht in „Dokumente" abgelegt.' });
            return;
        }
        let auftrag: UnterlageAblegen;
        try {
            auftrag = ufsaAblageAuftrag({
                projectId: projektId,
                datei: doc.output('blob') as Blob,
                projectData,
                entries,
                optionen: ausgabeOptionen(),
                anlass,
                gebucht: a.gebucht,
                vonHand: a.vonHand,
                abgelegtAmIso: ufsaHeuteIso(),
                // Bei der finalen Fassung legt der Kern den Journal-Eintrag mit
                // Steckbrief-Auszug an (Stufe 6.1); der Entwurf bekommt keinen.
                metadata: report.metadata,
            });
        } catch (error) {
            // Ohne Bytes gibt es nichts zu wiederholen — nur den Weg von Hand.
            setAblageStand({
                status: 'fehler', anlass, auftrag: null,
                fehler: `Das PDF konnte nicht ausgegeben werden: ${(error as Error)?.message || String(error)}`,
            });
            return;
        }
        void sendeAblageAuftrag(auftrag, anlass);
    };

    const ablageLaeuft = ablageStand?.status === 'laeuft';

    /**
     * Knopf „Entwurf ablegen" / „In Dokumente ablegen" — der eine Weg fuer
     * Entwuerfe (E17) und der Nachholweg fuer Fassungen. Setzt den aktuellen
     * Stand mit `output: 'doc'`, damit nicht noch einmal heruntergeladen wird.
     */
    const handleAblegenVonHand = async () => {
        if (ablageLaeuft) return;
        const anlass = ufsaAblageAnlassVonHand(!!projectData.isDraft);
        setAblageStand({ status: 'laeuft', anlass });
        try {
            const doc = await generateUnterlagePdf(projectData, entries, {
                ...ausgabeOptionen(),
                showTOC: false,
                output: 'doc',
            });
            legeStandAb(doc, anlass, { gebucht: false, vonHand: true });
        } catch (error) {
            setAblageStand({
                status: 'fehler', anlass, auftrag: null,
                fehler: `Das PDF konnte nicht gesetzt werden: ${(error as Error)?.message || String(error)}`,
            });
        }
    };

    // ═══════════════════════════════════════════════════════════
    // PDF GENERATION — Native jsPDF (replaces html2canvas)
    // ═══════════════════════════════════════════════════════════
    const executePdfGeneration = async (serviceIdToBill: string | null, hours: number = 1) => {
        setIsGeneratingPdf(true);
        setShowBillingModal(false);
        setExportBuchungHinweis(null);
        // Das Projekt des Vorgangs — beim KLICK festgehalten. Nach dem
        // `await` unten wird nur in dieses geschrieben (Codex R2c).
        const projektDesExports = projektDesVorgangs(abschluss.holeBericht() ?? report);
        // Erfolg nur nach tatsaechlich angenommenem Schreiben (Codex R3) —
        // bis dahin „nicht gebucht"; scheitert schon das PDF, bleibt es so.
        let buchung: ExportBuchungStand = { gebucht: false, hinweis: null };
        // Die Zweitschrift ins Buchungsjournal wird nur VORGEMERKT (diese
        // Ansicht speichert nicht selbst, sie reicht ueber onUpdate an die
        // Offline-Queue weiter) und bewusst erst HINTER dem try gestellt:
        // dessen catch zeigt dem Anwender einen PDF-Fehler, und ein Stolpern
        // der Zweitschrift darf nie so aussehen.
        let vormerkung: JournalMirrorRequest | null = null;
        // Das gesetzte Dokument fuer die Projektablage — ebenfalls erst HINTER
        // dem try: die Ablage darf weder als PDF-Fehler erscheinen noch die
        // Buchung aufhalten.
        let erzeugt: jsPDF | null = null;
        try {
            // Generate PDF using native jsPDF generator — no html2canvas needed
            erzeugt = await generateUnterlagePdf(projectData, entries, {
                showRegulations,
                showTOC: false,
                orientation: isExperimental ? 'landscape' : 'portrait',
                coverImageSize: projectData.coverImageSize || 'medium'
            });

            // --- BILLING LOGIC ---
            // Der Stand von JETZT, nicht der aus der Closure: waehrend das PDF
            // entstand, kann der Abschlussschritt einer frueheren Ablage ein
            // Paket gebucht haben (BST-…). Gelesen und geschrieben wird ueber
            // den gemeinsamen Ref-Schreibweg des Moduls (Codex R2c).
            const stand = abschluss.holeBericht() ?? report;
            if (serviceIdToBill) {
                const services = stand.metadata.globalData?.services || [];
                const serviceIndex = services.findIndex(s => s.id === serviceIdToBill);
                // Waehrend das PDF entstand, kann die Position verschwunden sein:
                // dann keine Buchung — und das wird gesagt, nicht verschwiegen.
                if (serviceIndex === -1) buchung = exportBuchungStand({ gewaehlt: serviceIdToBill, positionGefunden: false, verworfen: null });
                
                if (serviceIndex !== -1) {
                    const newServices = [...services];
                    const s = newServices[serviceIndex];
                    const increment = isHourlyUnit(s.unit) ? hours : 1;
                    const newUsed = (s.quantityUsed || 0) + increment;
                    const isDone = s.quantityContracted > 0 && newUsed >= s.quantityContracted;
                    
                    newServices[serviceIndex] = {
                        ...s,
                        quantityUsed: newUsed,
                        status: isDone ? 'done' : s.status
                    };
                    
                    const logEntry = {
                         id: crypto.randomUUID(),
                         date: new Date().toISOString(),
                         serviceId: s.id,
                         quantity: increment,
                         performerId: 'system',
                         performerName: stand.metadata.coordinator || 'System',
                         isSubstitution: false,
                         description: `Unterlage exportiert (${projectData.isDraft ? 'Entwurf' : 'Final'}) (${increment} ${s.unit})`
                    };
                    
                    const previousLog = stand.metadata.globalData?.serviceLog || [];
                    const verworfen = abschluss.schreibeFuer(projektDesExports, {
                        ...stand,
                        metadata: {
                            ...stand.metadata,
                            globalData: {
                                ...stand.metadata.globalData,
                                services: newServices,
                                serviceLog: [...previousLog, logEntry]
                            }
                        }
                    });
                    buchung = exportBuchungStand({ gewaehlt: serviceIdToBill, positionGefunden: true, verworfen });

                    // Diese Ansicht bekommt kein Projekt-Objekt, nur den
                    // Bericht: die echte Zeile in `projects` steht in
                    // `globalData.projectId` (dieselbe id, die unten die
                    // Projektablage benutzt). 'temp-id' ist das Kennzeichen
                    // eines noch nie gespeicherten Projekts — darunter wird
                    // NICHTS vorgemerkt.
                    const projektId = (stand.metadata.globalData?.projectId || '').trim();
                    // Nicht geschrieben (Projekt gewechselt, Ansicht verlassen) →
                    // auch nichts vormerken; der Hinweis folgt unten, fuer jeden
                    // Grund an EINER Stelle.
                    if (buchung.gebucht && projektId && projektId !== 'temp-id') {
                        vormerkung = {
                            projectId: projektId,
                            entries: [logEntry],
                            previousLog,
                            source: 'einweisung',
                            linkedProjectConfig: stand.metadata.globalData?.linkedProjectConfig ?? null,
                            // Sicher gewusst: ein Unterlagen-Export ist weder
                            // Zusatzleistung noch Nachweis einer BEGEHUNG.
                            statements: { [logEntry.id]: { isAddon: false, coversInspection: false } },
                        };
                    } else if (buchung.gebucht) {
                        console.warn('[UnterlageView] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
                    }
                }
            }
        } catch (error) {
            console.error("PDF Generation Error:", error);
            alert("Fehler bei der PDF-Erstellung: " + (error as Error).message);
        } finally {
            setIsGeneratingPdf(false);
        }
        if (buchung.hinweis) setExportBuchungHinweis(buchung.hinweis);
        if (vormerkung) queueJournalMirror(vormerkung);

        // Projektablage — nur, wenn das PDF wirklich entstanden ist, und nur
        // fuer Fassungen bzw. abgerechnete Entwuerfe (ufsaAblageAnlassFuerExport).
        // Nicht abgewartet: der Export ist fertig, die Ablage meldet sich
        // ueber ihren eigenen Stand unter der Leiste.
        if (erzeugt) {
            // Gebucht ist, was wirklich geschrieben wurde.
            const gebucht = buchung.gebucht;
            const anlass = ufsaAblageAnlassFuerExport({ isDraft: !!projectData.isDraft, gebucht });
            if (anlass) legeStandAb(erzeugt, anlass, { gebucht, vonHand: false });
        }
    };

    // ═══════════════════════════════════════════════════════════
    // VERSIONEN
    // ═══════════════════════════════════════════════════════════

    // Schreibt Versionsliste UND Dokumentinhalt in einem Zug — der verzögerte
    // Autospeicher-Timer darf das Ergebnis nicht überholen.
    const persistVersions = (nextVersions: UnterlageVersion[], nextProjectData: ProjectData, nextEntries: RabEntry[]) => {
        setVersions(nextVersions);
        onUpdate({
            ...report,
            unterlage: {
                projectData: nextProjectData,
                rabEntries: nextEntries,
                entries: []
            },
            unterlageVersions: nextVersions
        });
    };

    const handleSaveVersion = () => {
        const version = createUnterlageVersion(projectData, entries, versionLabel, new Date().toISOString(), crypto.randomUUID());
        persistVersions(appendUnterlageVersion(versions, version), projectData, entries);
        setVersionLabel('');
        setShowVersionDialog(false);
    };

    const handleLoadVersion = (version: UnterlageVersion) => {
        if (!window.confirm(`Version "${version.label}" laden?\n\nDer aktuelle Stand der Unterlage (Projektdaten und Maßnahmenkatalog) wird dabei ersetzt. Gespeicherte Versionen bleiben erhalten.`)) {
            return;
        }
        const restored = restoreUnterlageVersion(version, projectData.appendixImages);
        // Bringt die Version eine andere Anschrift mit, ist das eine Bearbeitung
        // in dieser Ansicht — sie geht wie bisher ins Projekt zurueck.
        if ((restored.projectData.builderAddress ?? '') !== (projectData.builderAddress ?? '')) anschriftBearbeitet.current = true;
        setProjectData(restored.projectData);
        setEntries(restored.rabEntries);
        persistVersions(versions, restored.projectData, restored.rabEntries);
        setShowVersionPanel(false);
    };

    const handleDeleteVersion = (version: UnterlageVersion) => {
        if (!window.confirm(`Version "${version.label}" löschen? Dieser Stand ist danach nicht mehr abrufbar.`)) {
            return;
        }
        persistVersions(removeUnterlageVersion(versions, version.id), projectData, entries);
    };

    const handleVersionPdf = async (version: UnterlageVersion) => {
        try {
            // Anhang aus dem laufenden Dokument ergänzen — dieselbe Regel wie
            // beim Laden einer Version, sonst fehlte der Anhang im PDF.
            const versionProjectData = withLiveAppendixImages(version.projectData, projectData.appendixImages);
            await generateUnterlagePdf(versionProjectData, version.rabEntries, {
                showRegulations,
                showTOC: false,
                orientation: isExperimental ? 'landscape' : 'portrait',
                coverImageSize: versionProjectData.coverImageSize || 'medium'
            });
        } catch (error) {
            console.error("PDF Generation Error:", error);
            alert("Fehler bei der PDF-Erstellung: " + (error as Error).message);
        }
    };

    const handleInitDialogChoice = (choice: 'edit' | 'new') => {
        if (choice === 'new') {
            // Vor dem Zurücksetzen den bisherigen Stand sichern — sonst wäre der
            // Maßnahmenkatalog unwiederbringlich verloren. Die Anhangsbilder
            // gehören nicht zur Version; auf sie weist der Dialog eigens hin.
            const now = new Date().toISOString();
            const backup = createUnterlageVersion(projectData, entries, autoBackupLabel(now), now, crypto.randomUUID());
            const nextProjectData: ProjectData = { ...projectData, appendixImages: [], version: '2.0', isDraft: true };
            setEntries([]);
            setProjectData(nextProjectData);
            persistVersions(appendUnterlageVersion(versions, backup), nextProjectData, []);
        }
        setShowInitDialog(false);
    };
    
    // Mitgeltende Unterlagen: liegen in projectData, Änderungen laufen damit
    // über denselben Autospeicher wie alle übrigen Projektdaten und gehen in
    // jede eingefrorene Version mit.
    const mitgeltendeUnterlagenPanel = (
        <MitgeltendeUnterlagenPanel
            unterlagen={projectData.mitgeltendeUnterlagen}
            entries={entries}
            onChange={(liste) => handleProjectChange('mitgeltendeUnterlagen', liste)}
            isReadOnly={isReadOnly}
        />
    );

    // Buchbare LV-Positionen: Zuordnung zuerst, Titel nur als Rueckfallebene.
    // Positionsnummern spielen bewusst keine Rolle mehr (siehe selectBillableServices).
    const billingServices = selectBillableServices(report.metadata.globalData?.services || [], 'unterlage');

    return (
        <div data-glass-view="module-unterlage" data-glass-bereich="module" className={`h-full flex flex-col ${isDarkMode ? 'dark bg-slate-900 text-white' : 'bg-slate-50 text-slate-900'} animate-fade-in relative`}>
            
            {showInitDialog && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <FileCheck className="w-6 h-6 text-blue-400"/> 
                                <h3 className="text-lg font-bold">Unterlage existiert bereits</h3>
                            </div>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl">
                                <p className="text-sm font-medium text-blue-900">
                                    Die Leistung "Unterlage erstellen" wurde bereits abgerechnet oder markiert. Was möchten Sie tun?
                                </p>
                            </div>
                            <div className="space-y-4">
                                <button onClick={() => handleInitDialogChoice('edit')} className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-blue-400 hover:bg-slate-50 transition-all group flex items-center justify-between">
                                    <div><h4 className="font-bold text-slate-800 text-sm flex items-center gap-2 group-hover:text-blue-600"><Edit2 className="w-4 h-4"/> Bestehende bearbeiten</h4><p className="text-xs text-slate-500 mt-1">Inhalte behalten.</p></div>
                                </button>
                                <div className="p-4 rounded-xl border-2 border-blue-100 bg-blue-50/30">
                                    <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2 mb-3"><Copy className="w-4 h-4 text-blue-600"/> Neue Version erstellen</h4>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Projektdaten bleiben, Maßnahmenkatalog und Anhangsbilder werden geleert. Der bisherige Katalog wird automatisch als Version gesichert — die Anhangsbilder nicht.
                                    </p>
                                    <Button variant="primary" onClick={() => handleInitDialogChoice('new')} className="w-full bg-blue-600 hover:bg-blue-700 shadow-md">Neu erstellen (Reset)</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {showVersionDialog && (
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
                        <div className="bg-slate-900 text-white p-5 flex items-center gap-3">
                            <Save className="w-6 h-6 text-blue-400"/>
                            <h3 className="text-lg font-bold">Version speichern</h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-600">
                                Der aktuelle Stand (Projektdaten und Maßnahmenkatalog) wird als eigene Version abgelegt. Bestehende Versionen bleiben unverändert.
                            </p>
                            <p className="text-xs text-slate-500">
                                Anhangsbilder gehören zum laufenden Dokument und werden nicht mit eingefroren: beim Laden einer Version und im Versions-PDF gelten immer die aktuellen Bilder.
                            </p>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Bezeichnung</label>
                                <input
                                    autoFocus
                                    type="text"
                                    value={versionLabel}
                                    onChange={(e) => setVersionLabel(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveVersion(); }}
                                    placeholder="z. B. Fassung zur Abnahme"
                                    className="w-full p-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                />
                                <p className="text-xs text-slate-400 mt-1">Ohne Eingabe wird Datum und Uhrzeit verwendet.</p>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="secondary" onClick={() => setShowVersionDialog(false)}>Abbrechen</Button>
                                <Button variant="primary" onClick={handleSaveVersion} className="bg-blue-600 hover:bg-blue-700">Speichern</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showBillingModal && (
                <BillingSelectionModal
                    isOpen={showBillingModal}
                    onClose={() => setShowBillingModal(false)}
                    onConfirm={executePdfGeneration}
                    services={billingServices}
                    title="Unterlage abrechnen"
                    context="unterlage"
                    memoryScope={report.metadata.globalData?.projectId}
                    canSeeFinancials={rechte.hat('preise.sehen')}
                    vorabHinweis={schonGebuchtHinweis(billingServices)}
                />
            )}

            {/* Sub-Header / Tool Bar */}
            <div data-glass="kopf" className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center sticky top-0 z-30 shadow-sm shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                   <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Modul-Tools</span>
                   {/* Pflichtstand aus dem Projektsteckbrief (§ 3 Abs. 2 Nr. 3 BaustellV).
                       Ein Lesefehler erscheint als „ungeklärt" mit Grund — nie als „keine". */}
                   <span
                       role="status"
                       title={pflicht.detail || undefined}
                       className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${PFLICHT_KLASSE[pflicht.ton]}`}
                   >
                       {pflicht.ton === 'laedt' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                       {pflicht.ton === 'pflicht' && <CheckCircle2 className="w-3.5 h-3.5" />}
                       {pflicht.ton === 'ungeklaert' && <AlertTriangle className="w-3.5 h-3.5" />}
                       {pflicht.text}
                   </span>
                </div>

                <div className="flex items-center gap-3">
                     <div className="relative">
                        <Button variant="secondary" onClick={() => setShowVersionPanel(v => !v)} className="text-xs flex items-center gap-2">
                            <History className="w-4 h-4" />
                            Versionen{versions.length > 0 ? ` (${versions.length})` : ''}
                        </Button>
                        {showVersionPanel && (
                            <div className="absolute right-0 top-full mt-2 w-[26rem] max-h-[70vh] overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-2xl z-40 animate-fade-in">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
                                    <h4 className="text-sm font-bold text-slate-800">Gespeicherte Versionen</h4>
                                    <button onClick={() => setShowVersionPanel(false)} className="p-1 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100"><X className="w-4 h-4" /></button>
                                </div>
                                <div className="p-3 space-y-2">
                                    {versions.length === 0 && (
                                        <p className="text-xs text-slate-500 px-1 py-6 text-center">Noch keine Version gespeichert.</p>
                                    )}
                                    {versions.map(v => (
                                        <div key={v.id} className="p-3 rounded-lg border border-slate-200 bg-slate-50 hover:border-blue-300 transition-colors">
                                            <div className="text-sm font-bold text-slate-800 break-words">{v.label}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">{formatVersionTimestamp(v.createdAt)} · {v.rabEntries.length} Maßnahmen</div>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <button onClick={() => handleLoadVersion(v)} className="px-2.5 py-1 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Version laden</button>
                                                <button onClick={() => handleVersionPdf(v)} className="px-2.5 py-1 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-md flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> PDF</button>
                                                <button onClick={() => handleDeleteVersion(v)} className="px-2.5 py-1 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-md flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Löschen</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                     </div>
                     <Button variant="secondary" onClick={() => { setVersionLabel(''); setShowVersionDialog(true); }} className="text-xs flex items-center gap-2">
                        <Save className="w-4 h-4" /> Version speichern
                     </Button>
                     <div className="h-6 w-px bg-slate-200 mx-1"></div>
                     <Button variant="secondary" onClick={() => setDokumenteOffen(true)} className="text-xs flex items-center gap-2" title="Pläne, Verträge und Nachweise aus der Projektablage">
                        <FolderOpen className="w-4 h-4" /> Dokumente
                     </Button>
                     <div className="h-6 w-px bg-slate-200 mx-1"></div>
                     <button onClick={() => setShowWiki(true)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors" title="Hilfe & Wiki"><BookOpen className="w-5 h-5" /></button>
                     <button onClick={() => setShowSettings(true)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors" title="Einstellungen"><SettingsIcon className="w-5 h-5" /></button>
                     <div className="h-6 w-px bg-slate-200 mx-1"></div>
                     {currentTab === 'print' && (
                        <div className="flex gap-2">
                             <Button variant="secondary" onClick={() => setShowDesignSelector(true)} className="text-xs">Design</Button>
                             {/* Der eine Weg fuer Entwuerfe in die Ablage (E17) — und der
                                 Nachholweg fuer Fassungen, deren Export ohne Ablage blieb. */}
                             <Button
                                variant="secondary"
                                onClick={() => void handleAblegenVonHand()}
                                disabled={isReadOnly || ablageLaeuft || isGeneratingPdf}
                                title={isReadOnly
                                    ? readOnlyTitle('Die Unterlage wird nicht in „Dokumente" abgelegt.')
                                    : projectData.isDraft
                                        ? 'Den aktuellen Stand als ENTWURF in „Dokumente" ablegen'
                                        : 'Die aktuelle Fassung in „Dokumente" ablegen'}
                                className="text-xs flex items-center gap-2"
                             >
                                {ablageLaeuft ? <Loader2 className="w-3 h-3 animate-spin"/> : <FolderInput className="w-3 h-3"/>}
                                {projectData.isDraft ? 'Entwurf ablegen' : 'In Dokumente ablegen'}
                             </Button>
                             <Button variant="primary" onClick={handleGenerateClick} disabled={isGeneratingPdf} className="bg-blue-600 hover:bg-blue-700 text-xs flex items-center gap-2">
                                {isGeneratingPdf ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}
                                {isGeneratingPdf ? '...' : 'PDF'}
                            </Button>
                        </div>
                     )}
                </div>
            </div>

            <main className={`flex-1 overflow-y-auto p-6 md:p-8 relative ${currentTab === 'print' ? 'print-wrapper-hook' : ''}`}>
                <div className="max-w-7xl mx-auto">

                    {/* Aenderungsdienst (Stufe 6.1): weicht die finale Fassung vom heutigen
                        Stand der Planungsphase ab, steht die Rueckfrage im Kopf — auf jedem
                        Reiter. Nur lesend; geschrieben wird dort allein der Entscheid. */}
                    {projektIdFuerPlanung && projektIdFuerPlanung !== 'temp-id' && (
                        <AbweichungsHinweis
                            projectId={projektIdFuerPlanung}
                            metadata={report.metadata}
                            unterlage="unterlage"
                            isReadOnly={isReadOnly}
                            onNeueFassung={handleNeueFassungAusPlanung}
                        />
                    )}
                    {neueFassungHinweis && (
                        <div className="mb-4 p-3 rounded-xl border text-xs flex items-start gap-2 bg-blue-50 border-blue-200 text-blue-900">
                            <span className="flex-1">
                                Neue Fassung: Den bisherigen Stand als Version sichern, dann die Unterlage bearbeiten
                                (Karten „Aus der Planungsphase" zeigen die geaenderten Werte) und erneut als PDF exportieren.
                                Der Export ist die neue Fassung; die bisherige bleibt als Version und in „Dokumente".
                            </span>
                            <button onClick={() => setNeueFassungHinweis(false)} className="p-1 shrink-0" title="Hinweis ausblenden"><X className="w-3.5 h-3.5" /></button>
                        </div>
                    )}

                    {/* Stand der Projektablage — auf jedem Reiter sichtbar, denn der
                        Export laeuft vom Druck-Reiter aus, gelesen wird ueberall. */}
                    {ablageStand && (
                        <div className={`mb-4 p-3 rounded-xl border text-xs flex items-start gap-2 ${
                            ablageStand.status === 'fehler'
                                ? 'bg-red-50 border-red-200 text-red-700'
                                : ablageStand.status === 'uebersprungen' || (ablageStand.status === 'schon_abgelegt' && ablageStand.warnung)
                                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                                    : ablageStand.status === 'laeuft'
                                        ? 'bg-slate-50 border-slate-200 text-slate-600'
                                        : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        }`}>
                            {ablageStand.status === 'laeuft' && <Loader2 className="w-4 h-4 shrink-0 mt-px animate-spin" />}
                            {(ablageStand.status === 'abgelegt' || (ablageStand.status === 'schon_abgelegt' && !ablageStand.warnung)) && <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" />}
                            {(ablageStand.status === 'fehler' || ablageStand.status === 'uebersprungen' || (ablageStand.status === 'schon_abgelegt' && ablageStand.warnung)) && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
                            <span className="flex-1">
                                {ablageStand.status === 'laeuft' && 'Wird in „Dokumente" abgelegt…'}
                                {ablageStand.status === 'abgelegt' && <>In „Dokumente" abgelegt: <span className="font-semibold">{ablageStand.titel}</span></>}
                                {ablageStand.status === 'schon_abgelegt' && (
                                    <>
                                        Dieser Stand liegt bereits in „Dokumente": <span className="font-semibold">{ablageStand.titel}</span> — es wurde nichts doppelt abgelegt.
                                        {/* Der Kern konnte die doppelt hochgeladene Datei nicht wieder
                                            entfernen: liegt, aber mit Rest im Bucket — gelb, kein Retry. */}
                                        {ablageStand.warnung && <> <span className="font-semibold">{ablageStand.warnung}</span></>}
                                    </>
                                )}
                                {ablageStand.status === 'fehler' && (
                                    <>
                                        Nicht in „Dokumente" abgelegt: {ablageStand.fehler} PDF und Buchung bleiben gültig.
                                        {ablageStand.auftrag
                                            ? ' „Erneut ablegen" legt genau das exportierte Blatt ab.'
                                            : ' Über „In Dokumente ablegen" lässt sich der aktuelle Stand ablegen.'}
                                    </>
                                )}
                                {ablageStand.status === 'uebersprungen' && ablageStand.grund}
                                {/* Hinweise des Kerns zu einer gelungenen Ablage — keine Fehler, dezent darunter. */}
                                {(ablageStand.status === 'abgelegt' || ablageStand.status === 'schon_abgelegt') && ablageStand.hinweise.length > 0 && (
                                    <ul className="mt-1 space-y-0.5 text-[11px] font-normal text-slate-600">
                                        {ablageStand.hinweise.map((h, i) => <li key={i}>{h}</li>)}
                                    </ul>
                                )}
                            </span>
                            {ablageStand.status === 'fehler' && ablageStand.auftrag && (
                                <button
                                    onClick={() => void sendeAblageAuftrag(ablageStand.auftrag, ablageStand.anlass)}
                                    className="shrink-0 px-2.5 py-1 font-bold text-red-700 bg-white border border-red-200 hover:bg-red-100 rounded-md flex items-center gap-1.5"
                                    title={'Genau das exportierte Blatt erneut in „Dokumente" ablegen'}
                                >
                                    <RotateCw className="w-3.5 h-3.5" /> Erneut ablegen
                                </button>
                            )}
                            {(ablageStand.status === 'abgelegt' || ablageStand.status === 'schon_abgelegt') && (
                                <button
                                    onClick={() => setDokumenteOffen(true)}
                                    className="shrink-0 px-2.5 py-1 font-bold text-emerald-800 bg-white border border-emerald-200 hover:bg-emerald-100 rounded-md flex items-center gap-1.5"
                                >
                                    <FolderOpen className="w-3.5 h-3.5" /> Dokumente öffnen
                                </button>
                            )}
                            {ablageStand.status !== 'laeuft' && (
                                <button onClick={() => setAblageStand(null)} className="p-1 shrink-0" title="Hinweis ausblenden"><X className="w-3.5 h-3.5" /></button>
                            )}
                        </div>
                    )}
                    {/* Plan B5, Fall 2/3: was die Freigabe an Bestandteilen erledigt hat. */}
                    <BestandteilAbschlussHinweis stand={abschluss.stand} onSchliessen={abschluss.zuruecksetzen} className="mb-4" />
                    {/* Stunden-Pflichtfenster (Stundenkontingent S3) — genau einmal je Modul. */}
                    <BestandteilStundenPflicht pflicht={abschluss.stundenPflicht} />
                    {exportBuchungHinweis && (
                        <div className="mb-4 p-3 rounded-xl border text-xs flex items-start gap-2 bg-amber-50 border-amber-200 text-amber-900">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                            <span className="flex-1">{exportBuchungHinweis}</span>
                            <button onClick={() => setExportBuchungHinweis(null)} className="p-1 shrink-0" title="Hinweis ausblenden"><X className="w-3.5 h-3.5" /></button>
                        </div>
                    )}

                    {currentTab === 'project' && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                             <UfsaProjektdatenHinweis
                                abweichungen={abweichungen}
                                isReadOnly={isReadOnly}
                                onUebernehmen={handleProjektdatenUebernehmen}
                             />
                             <ProjectForm
                                data={projectData} 
                                onChange={handleProjectChange} 
                                defaultPreamble={defaultPreamble}
                             />
                        </div>
                    )}

                    {currentTab === 'catalog' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <UfsaPlanungFragenKarten
                                fragen={planung.fragen}
                                bereit={planung.bereit}
                                fehler={planung.fehler}
                                isReadOnly={isReadOnly}
                                onVorbelegen={handleFrageVorbelegen}
                                onNeuLaden={planung.neuLaden}
                            />
                            <EntryForm
                                onAdd={handleAddEntry}
                                onAddMultiple={handleAddMultipleEntries}
                                existingEntries={entries}
                                projectId={report.metadata.globalData?.projectId || ''}
                                gebaeudetyp={gebaeudetyp}
                                vorbelegung={vorbelegung}
                            />
                            <div className="mt-8">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-500" /> Erfasste Maßnahmen</h3>
                                    {entries.length > 0 && (
                                        <button 
                                            onClick={handleClearCatalog}
                                            className="px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors flex items-center gap-1.5"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Katalog leeren
                                        </button>
                                    )}
                                </div>
                                <EntryList entries={entries} onDelete={handleDeleteEntry} onEdit={handleUpdateEntry} onMove={handleMoveEntry} onMoveToCategory={handleMoveEntryToCategory} onCategoryRename={handleRenameCategory} categoryOrder={categoryOrder} />
                            </div>
                        </div>
                    )}

                    {/* Eigener Abschnitt (Sidebar: „Mitgeltende Unterlagen“
                        direkt nach dem Maßnahmenkatalog — wie im PDF, wo das
                        Kapitel auf den Katalog folgt). */}
                    {currentTab === 'mitgeltend' && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
                            {mitgeltendeUnterlagenPanel}
                            {/* Unterlagen, die die Planungsphase als vorliegend fuehrt (`liegt_vor`,
                                nur `ufsa`-relevante) — Uebernahme in die Liste nur per Klick. */}
                            <UfsaPlanungUnterlagenVorschlaege
                                unterlagen={planung.steckbrief?.unterlagen ?? null}
                                gespeichert={projectData.mitgeltendeUnterlagen}
                                onChange={(liste) => handleProjectChange('mitgeltendeUnterlagen', liste)}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                    )}

                    {currentTab === 'appendix' && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <AppendixManager 
                                projectData={projectData}
                                entries={entries}
                                onUpdate={(imgs) => handleProjectChange('appendixImages', imgs)}
                                onAddEntry={handleAddEntry}
                            />
                        </div>
                    )}

                    {currentTab === 'print' && (
                        <div className="animate-in fade-in duration-300">
                            {/* Die Vorschau IST das PDF — keine HTML-Nachbildung mehr,
                                die eigene Umbrüche riet und anders aussah als der Export. */}
                            <UfsaPdfPreview
                                projectData={projectData}
                                entries={entries}
                                showRegulations={showRegulations}
                                coverImageSize={projectData.coverImageSize || 'medium'}
                                orientation={isExperimental ? 'landscape' : 'portrait'}
                                onDownload={handleGenerateClick}
                                downloadDisabled={isGeneratingPdf}
                            />
                        </div>
                    )}

                </div>
            </main>
            
            <AiAssistant analysis={analysis} onNavigateToFix={handleFixNavigation} />

            {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} />}
            {showWiki && <WikiModal onClose={() => setShowWiki(false)} />}
            
            {showSettings && (
                <SettingsModal onClose={() => setShowSettings(false)} isDarkMode={isDarkMode} toggleDarkMode={() => setIsDarkMode(!isDarkMode)} isExperimental={isExperimental} toggleExperimental={toggleLandscape} categoryOrder={categoryOrder} onUpdateCategoryOrder={setCategoryOrder} coverImageSize={projectData.coverImageSize || 'medium'} onChangeCoverImageSize={(size) => handleProjectChange('coverImageSize', size)} />
            )}

            {/* Bewusst OHNE Bedingung auf die Projekt-Id: fehlte sie, passierte
                nach dem Klick sonst kommentarlos nichts. Das Panel erkennt ein
                noch nicht gespeichertes Projekt selbst (auch 'temp-id') und
                sagt es. */}
            {dokumenteOffen && (
                <DokumentePanel
                    projectId={report.metadata.globalData?.projectId || ''}
                    modul="unterlage"
                    userProfile={userProfile}
                    isReadOnly={isReadOnly}
                    onSchliessen={() => setDokumenteOffen(false)}
                />
            )}

            {showDesignSelector && (
                <CoverDesignSelector currentDesignId={projectData.designId || 'classic'} themeColor={projectData.themeColor || '#2563eb'} projectData={projectData} onSelectDesign={(id) => handleProjectChange('designId', id)} onSelectColor={(color) => handleProjectChange('themeColor', color)} onClose={() => setShowDesignSelector(false)} />
            )}
        </div>
    );
};